import { SyllabusStoreModel, TopicLevel2StoreModel } from '@examdojo/category/v2';
import { ExamCourse } from '@examdojo/models/exam';
import { StemMarkScheme } from '@examdojo/models/markscheme-v2';
import { QuestionItemV2 } from '@examdojo/models/question';
import { EntityState } from '@examdojo/state';
import { Constants, TableInsertModel, TableRowModel, TableUpdateModel, ViewRowModel } from '@examdojo/supabase';

export type ReferenceQuestionHttpModel = Pick<
  TableRowModel<'reference_questions'>,
  | 'created_at'
  | 'id'
  | 'question_labels'
  | 'question_number'
  | 'source_ref'
  | 'status'
  | 'status_reason'
  | 'created_by'
  | 'llm_feedback'
  | 'parsed_markscheme'
  | 'parsed_markscheme_llm_feedback'
  | 'human_feedback'
  | 'question_items'
  | 'source_id'
  | 'metadata'
  | 'question_number'
>;

export type ReferenceQuestionViewHttpModel = Pick<
  ViewRowModel<'reference_question_items_view'>,
  | 'created_at'
  | 'id'
  | 'status'
  | 'source_id'
  | 'source_name'
  | 'source_status'
  | 'syllabus_id'
  | 'topics'
  | 'total_marks'
  | 'total_stems'
  | 'updated_at'
  | 'question_image_count'
  | 'markscheme_image_count'
  | 'published_derivative_questions'
  | 'source_id'
  | 'question_number'
  | 'type'
>;

export const REFERENCE_QUESTION_STATUS = Constants.public.Enums.reference_question_status;

export type ReferenceQuestionStatus = (typeof REFERENCE_QUESTION_STATUS)[number];

export interface ReferenceQuestionLlmFeedback {
  is_fully_correct: boolean;
  markscheme_additional_feedback: string;
  markscheme_clearly_readable: boolean;
  markscheme_no_missing_parts: boolean;
  markscheme_right_order: boolean;
  question_additional_feedback: string;
  question_clearly_readable: boolean;
  question_no_missing_parts: boolean;
  question_right_order: boolean;
}

export interface ReferenceQuestionParsedMarkschemeLlmFeedback {
  points_add_up: boolean;
  is_fully_correct: boolean;
  no_missing_parts: boolean;
  additional_feedback: string;
  math_equations_correct: boolean;
  points_add_up_feedback: string;
  no_missing_parts_feedback: string;
  math_equations_correct_feedback: string;
}

const ReferenceQuestionStatusLabels: Record<ReferenceQuestionStatus, string> = {
  DELETED: 'Deleted',
  UNFIXABLE: 'Unfixable',
  DEPRECATED_HUMAN_APPROVED: 'Deprecated Human Approved',
  NEEDS_QUESTION_PARSING: 'Needs Question Parsing',
  NEEDS_QUESTION_INTERNAL_REVIEW: 'Needs Question Internal Review',
  NEEDS_QUESTION_REVIEW: 'Needs Question Review',
  NEEDS_MARKSCHEME_PARSING: 'Needs Markscheme Parsing',
  NEEDS_MARKSCHEME_REVIEW: 'Needs Markscheme Review',
  NEEDS_IMAGE_PARSING: 'Needs Image Parsing',
  NEEDS_IMAGE_REVIEW: 'Needs Image Review',
  IMAGE_NOT_PARSEABLE: 'Image Not Parseable',
  NEEDS_TOPIC_TAGGING: 'Needs Topic Tagging',
  HUMAN_APPROVED: 'Human Approved',
  NEEDS_EXPERT_MARKSCHEME_REVIEW: 'Needs Expert Markscheme Review',
  NEEDS_EXPERT_IMAGE_REVIEW: 'Needs Expert Image Review',
};

const ReferenceQuestionStatusClassNames: Record<ReferenceQuestionStatus, string> = {
  DELETED: 'bg-red-100 text-red-600',
  UNFIXABLE: 'bg-red-100 text-red-600',
  DEPRECATED_HUMAN_APPROVED: 'bg-teal-100 text-teal-600',
  NEEDS_QUESTION_PARSING: 'bg-amber-100 text-amber-600',
  NEEDS_QUESTION_INTERNAL_REVIEW: 'bg-amber-100 text-amber-600',
  NEEDS_QUESTION_REVIEW: 'bg-amber-100 text-amber-600',
  NEEDS_MARKSCHEME_PARSING: 'bg-orange-100 text-orange-600',
  NEEDS_MARKSCHEME_REVIEW: 'bg-orange-100 text-orange-600',
  NEEDS_IMAGE_PARSING: 'bg-amber-100 text-amber-600',
  NEEDS_IMAGE_REVIEW: 'bg-amber-100 text-amber-600',
  IMAGE_NOT_PARSEABLE: 'bg-amber-100 text-amber-600',
  NEEDS_TOPIC_TAGGING: 'bg-amber-100 text-amber-600',
  HUMAN_APPROVED: 'bg-green-100 text-green-600',
  NEEDS_EXPERT_MARKSCHEME_REVIEW: 'bg-yellow-100 text-yellow-600',
  NEEDS_EXPERT_IMAGE_REVIEW: 'bg-yellow-100 text-yellow-600',
};

export function getReferenceQuestionStatusClassName(status: ReferenceQuestionStatus | null): string {
  return status ? ReferenceQuestionStatusClassNames[status] : 'bg-yellow-300';
}

export function getReferenceQuestionStatusLabel(status: ReferenceQuestionStatus | null | undefined): string {
  return status ? ReferenceQuestionStatusLabels[status] : 'Submitted';
}

export const ReferenceQuestionStatusList = [...Object.values(REFERENCE_QUESTION_STATUS), null];

export type ReferenceQuestionStoreModel = Omit<
  ReferenceQuestionHttpModel,
  | 'sys_period'
  | 'status'
  | 'llm_feedback'
  | 'parsed_markscheme'
  | 'parsed_markscheme_llm_feedback'
  | 'question_items'
  | 'metadata'
> & {
  source_ref: SourceRef;
  status: ReferenceQuestionStatus | null;
  llm_feedback: ReferenceQuestionLlmFeedback | null;
  parsed_markscheme_llm_feedback: ReferenceQuestionParsedMarkschemeLlmFeedback | null;
  parsed_markscheme: StemMarkScheme | null;
  question_items: QuestionItemV2[];
  metadata: {
    pdf_markscheme_page_number?: number;
    pdf_question_page_number?: number;
    max_marks?: number;
    multiple_choice?: boolean;
    calculator_allowed?: boolean;
  } | null;
};

export type ReferenceQuestionUIModel = ReferenceQuestionStoreModel;

export interface ReferenceQuestionState extends EntityState<ReferenceQuestionStoreModel> {
  props: {
    syllabusMap: Record<ReferenceQuestionStoreModel['id'], SyllabusStoreModel['id']>;
  };
}

export const REFERENCE_QUESTION_CREATE_ALLOWED_KEYS = [
  'question_labels',
  'source_ref',
  'human_feedback',
  'question_items',
  'question_number',
  'status',
  'metadata',
] satisfies Array<keyof TableInsertModel<'reference_questions'>>;

export type ReferenceQuestionCreateModel = Pick<
  TableInsertModel<'reference_questions'>,
  (typeof REFERENCE_QUESTION_CREATE_ALLOWED_KEYS)[number]
>;

export const REFERENCE_QUESTION_UPDATE_ALLOWED_KEYS = REFERENCE_QUESTION_CREATE_ALLOWED_KEYS;

export type ReferenceQuestionUpdateModel = Pick<
  TableUpdateModel<'reference_questions'>,
  (typeof REFERENCE_QUESTION_UPDATE_ALLOWED_KEYS)[number]
>;

export interface SourceRef {
  type: ReferenceQuestionSourceType;
  course: ExamCourse;
  year: string;
  month: string;
  timezone: string;
  paper: string;
}

export const REFERENCE_EXAM_MONTHS = ['May', 'November'] as const;
export type ReferenceExamMonth = (typeof REFERENCE_EXAM_MONTHS)[number];

export const REFERENCE_EXAM_TIMEZONES = ['TZ1', 'TZ2'];
export type ReferenceExamTimezone = (typeof REFERENCE_EXAM_TIMEZONES)[number];

export enum ReferenceQuestionSourceType {
  IbMathExam = 'ib_math_exam',
  RevisionVillage = 'revisionvillage',
}

export type ReferenceQuestionViewStoreModel = Omit<ReferenceQuestionViewHttpModel, 'topics'> & {
  topics: TopicLevel2StoreModel[];
};

export const REFERENCE_QUESTION_STORAGE_BUCKET = 'reference_questions';
